import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function ExeterPage() {
  return (
    <>
      <LocalBusinessSchema city="Exeter" latitude={50.7184} longitude={-3.5339} />
      <PageHero
        title="Fractional CISO Services in Exeter"
        subtitle="Cybersecurity leadership for Devon's innovation corridor"
        eyebrow="Exeter"
        seoTitle="Fractional CISO Exeter | The CISO Network"
        seoDescription="Expert fractional CISO services for Exeter businesses. Access senior cybersecurity leadership for South West tech and professional services."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the South West's Rising Tech Hub
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Exeter has rapidly established itself as one of the South West's most dynamic
                  business centres, with a thriving ecosystem spanning financial services,
                  environmental technology, digital health, and professional services. The city's
                  Science Park, combined with the neighbouring Exeter and East Devon Enterprise
                  Zone, has created a fertile ground for innovation and growth.
                </p>
                <p>
                  South West businesses face distinct cybersecurity challenges. Exeter's financial
                  services firms, including several major insurers and investment managers, must
                  maintain robust security programmes. The region's growing environmental tech
                  sector handles sensitive data from global clients. Meanwhile, the city's
                  healthcare and digital health businesses navigate strict NHS Digital and data
                  protection requirements.
                </p>
                <p>
                  Many Exeter businesses find themselves caught between needing senior security
                  leadership and being unable to justify a full-time CISO hire. The regional talent
                  market is competitive, and London-based consultants rarely understand the local
                  business context. Our fractional CISO service provides Exeter businesses with
                  accessible, locally-aware security leadership.
                </p>
                <p>
                  Exeter and Devon's economy blends public sector, defence, tourism, and rural
                  enterprises. Many Devon businesses lack the internal IT resources of city-based
                  counterparts but face identical cyber threats. A fractional CISO provides
                  proportionate, practical security leadership that respects rural business
                  realities and budgets.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the South West business landscape, from the
                      city centre to the Science Park.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Devon & Cornwall Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Exeter, Plymouth, and the wider South West
                      peninsula.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Exeter Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Accessible security leadership for Devon's growing businesses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Healthcare Ready',
                description:
                  'Meet NHS Digital and healthcare data protection requirements with confidence.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Enterprise-grade CISO expertise without the London price tag.',
              },
              {
                icon: Users,
                title: 'Regional Understanding',
                description:
                  'CISOs who understand South West business dynamics and client expectations.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description:
                  'Scale security leadership as your Exeter business grows into new markets.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Exeter - FAQs"
        subtitle="Common questions from Exeter and Devon businesses"
        items={[
          {
            id: 'exeter-1',
            question: 'How does a fractional CISO work with businesses in Exeter?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Exeter. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing public sector, defence, and tourism companies.',
          },
          {
            id: 'exeter-2',
            question: 'What size of business benefits from a fractional CISO in Exeter?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Exeter startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'exeter-3',
            question: 'What industries do your CISOs cover in Exeter?',
            answer:
              "Our network includes CISOs with experience in public sector, defence, and tourism. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'exeter-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'exeter-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Exeter Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your Devon
            business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
